import { useEffect, useMemo, type ReactNode } from "react";
import { EMOTES, UI_FRAME_ART, cosmeticById, itemById, type Resource } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { AssetIcon } from "../assets/Icon.js";
import { Timer } from "../ui/Timer.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { useBalances } from "../state/balances.js";
import { unitName } from "../units/shared.js";
import { useStore } from "./state.js";
import type { Bundle, StoreCard } from "./types.js";
import { audio } from "../audio/engine.js";
import { cardArtAsset } from "./art.js";
import { useServerTick } from "../ui/Timer.js";
import { GameIcon, ResourceIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { Dialog } from "../ui/Dialog.js";
import { RewardFlight } from "../ui/RewardFlight.js";
import "./bundle.css";

function BundleCosmeticArt({ id, size }: { id: string; size: number }) {
  const frame = UI_FRAME_ART[id as keyof typeof UI_FRAME_ART];
  if (frame) return <GameIcon id={frame} size={size} />;
  const emote = EMOTES.find((entry) => entry.pack === id);
  if (emote) return <GameIcon id={`emote.${emote.id}`} size={size} />;
  // Model cosmetics use their actual portrait; other rewards retain their rendered package art.
  return <CardArt art={id} size={size} />;
}

function nameOf(id: string): string {
  if (id.startsWith("unit.")) return unitName(id);
  if (id.startsWith("cos.")) return pick(cosmeticById(id).name);
  try {
    return pick(itemById(id).name);
  } catch {
    return id;
  }
}

/** One line per bundle entry: icon, name and quantity. */
export function BundleList({ bundle, compact = false, preview = false }: { bundle: Bundle; compact?: boolean; preview?: boolean }) {
  const t = useT();
  const rows: { key: string; icon: ReactNode; text: string; qty: string }[] = [];
  for (const [k, v] of Object.entries(bundle.resources ?? {}))
    rows.push({
      key: `r-${k}`,
      icon: <ResourceIcon resource={k as Resource} size={compact ? 22 : 30} />,
      text: t(`res.${k}`),
      qty: formatCompact(v),
    });
  for (const i of bundle.items ?? [])
    rows.push({
      key: `i-${i.itemId}`,
      icon: <AssetIcon id={i.itemId} size={compact ? 22 : 30} />,
      text: nameOf(i.itemId),
      qty: `×${i.qty}`,
    });
  for (const u of bundle.units ?? [])
    rows.push({
      key: `u-${u}`,
      icon: <AssetIcon id={u} size={compact ? 22 : 30} portrait />,
      text: unitName(u),
      qty: "",
    });
  for (const c of bundle.cosmetics ?? [])
    rows.push({ key: `c-${c}`, icon: <BundleCosmeticArt id={c} size={compact ? 22 : 30} />, text: nameOf(c), qty: "" });
  if (bundle.credits)
    rows.push({
      key: "credits",
      icon: <ResourceIcon resource="CREDITS" size={compact ? 22 : 30} />,
      text: t("store.credits"),
      qty: `+${bundle.credits}`,
    });
  if (bundle.vipPoints)
    rows.push({
      key: "vip",
      icon: <StatIcon stat="star" size={compact ? 22 : 30} />,
      text: t("store.vipPoints"),
      qty: `+${bundle.vipPoints}`,
    });
  const renderRow = (r: (typeof rows)[number]) => (
    <li key={r.key} className="row bundle-row">
      <span className="bundle-icon">{r.icon}</span>
      <span className="bundle-label">{r.text}</span>
      <span className="mono dim">{r.qty}</span>
    </li>
  );
  if (preview) {
    // A unit or cosmetic must not disappear behind a long resource list.
    const priority = (key: string) => key.startsWith("u-") || key.startsWith("c-") ? 0 : key === "credits" || key === "vip" ? 1 : 2;
    const highlights = [...rows].sort((a, b) => priority(a.key) - priority(b.key));
    return <ul className="bundle bundle-preview" aria-label={t("store.redesign.contents")}>
      {highlights.slice(0, 4).map(r => <li key={r.key} title={`${r.text} ${r.qty}`} aria-label={`${r.text} ${r.qty}`}><span className="bundle-preview-art">{r.icon}</span><strong>{r.qty || "1"}</strong></li>)}
      {highlights.length > 4 ? <li className="bundle-preview-more" title={t("store.redesign.moreRewards", { n: highlights.length - 4 })}>+{highlights.length - 4}</li> : null}
    </ul>;
  }
  return (
    <ul className={`bundle ${compact ? "compact" : ""}`}>
      {rows.map(renderRow)}
    </ul>
  );
}

export function CardArt({ art, size = 56 }: { art: string | null; size?: number }) {
  const asset = cardArtAsset(art);
  return <AssetIcon id={asset.id} size={size} portrait={asset.portrait} className="card-art" />;
}

/** Price with the "minutes of play" hint (one Credit per real minute, D06). */
export function Price({ price, currency }: { price: number; currency: string }) {
  const t = useT();
  if (currency !== "CREDITS")
    return (
      <span className="mono price">
        {price} {currency}
      </span>
    );
  return (
    <span className="mono price">
      <span className="gold"><ResourceIcon resource="CREDITS" label={t("store.credits")} /> {price}</span>
      <span className="dim price-hint">
        {price >= 60
          ? t("store.hours", { n: Math.round((price / 60) * 10) / 10 })
          : t("store.minutes", { n: price })}
      </span>
    </span>
  );
}

/** A store card: art, name, contents, value ribbon, limit, countdown and the buy button. */
export function OfferCard({
  card,
  onBuy,
  testId,
  extra,
  creditBalance,
}: {
  card: StoreCard;
  onBuy?: (card: StoreCard) => void;
  testId?: string;
  extra?: ReactNode;
  /** Read-only presentation input for isolated component previews. */
  creditBalance?: number;
}) {
  const t = useT();
  const now = useServerTick();
  const accountCredits = useBalances((s) => s.balances?.credits ?? 0);
  const credits = creditBalance ?? accountCredits;
  const openConfirm = useStore((s) => s.openConfirm);
  const soldOut = card.limit ? card.limit.used >= card.limit.max : false;
  const locked = card.locked !== null;
  const expired = card.expiresAt !== null && card.expiresAt <= now;
  const affordable = card.currency !== "CREDITS" || credits >= card.price;
  const buy = onBuy ?? ((c: StoreCard) => openConfirm(c));
  return (
    <article
      className={`offer reward-offer ${card.double ? "double" : ""} ${soldOut || locked ? "sold" : ""}`}
      data-testid={testId ?? `offer-${card.key}`}
      data-key={card.key}
      data-expires-at={card.expiresAt ?? undefined}
      data-remaining={card.limit ? Math.max(0, card.limit.max - card.limit.used) : undefined}
    >
      <div className={`offer-visual ${card.double ? "is-double" : ""}`}>
        <CardArt art={card.art} size={112} />
        <div className="offer-tags">
          {card.discountPct > 0 ? <span className="offer-saving">−{card.discountPct}%</span> : null}
          {card.double ? <span className="offer-double">2×</span> : null}
        </div>
        {card.expiresAt ? <span className="offer-deadline"><Icon name="clock" size={13} />{expired ? t("store.expired") : <><span>{t("store.redesign.ends")}</span> <Timer finishesAt={card.expiresAt} /></>}</span> : null}
      </div>
      <div className="offer-heading">
        <div className="offer-kicker">{card.step ? t("store.chainStep", { n: card.step }) : card.double ? t("store.redesign.double") : t("store.redesign.standard")}</div>
        <h3 className="offer-name">{pick(card.name)}</h3>
        {card.limit ? <span className="offer-stock">{pick({ de: "Noch", en: "Remaining" })}: {Math.max(0, card.limit.max - card.limit.used)}</span> : null}
      </div>
      <BundleList bundle={card.contents} compact preview />
      {extra}
      <div className="offer-footer">
        <button type="button" className="plain offer-inspect" onClick={() => buy(card)} data-testid={`contents-${card.key}`}>{t("store.redesign.contents")} <Icon name="chevronRight" size={14} /></button>
        <Button
          variant={card.double ? "gold" : "primary"}
          disabled={soldOut || locked || expired || !affordable}
          onClick={() => buy(card)}
          data-testid={`buy-${card.key}`}
          title={!affordable ? t("store.notEnough") : undefined}
        >
          {expired ? (
            t("store.expired")
          ) : locked ? (
            t("store.locked")
          ) : soldOut ? (
            t("store.soldOut")
          ) : (
            <><span className="offer-choose">{t("store.redesign.choose")}</span><Price price={card.price} currency={card.currency} /></>
          )}
        </Button>
        <span className="offer-value">{!affordable && !soldOut && !locked && !expired ? t("store.notEnough") : (card.contents.vipPoints ?? 0) > 0 ? `+${card.vipPoints + (card.contents.vipPoints ?? 0)} ${t("store.vipPoints")}` : card.value > 0 && card.currency === "CREDITS" ? t("store.worth", { n: card.value }) : ""}</span>
      </div>
    </article>
  );
}

/** Confirm sheet: what you get for how many Credits, then the purchase. */
export function PurchaseSheet() {
  const t = useT();
  const card = useStore((s) => s.confirm);
  const openConfirm = useStore((s) => s.openConfirm);
  const buy = useStore((s) => s.buy);
  const credits = useBalances((s) => s.balances?.credits ?? 0);
  const purchasing = useStore((s) => s.purchasing);
  const now = useServerTick();
  if (!card) return null;
  return <Dialog testId="purchase-sheet" title={<span className="purchase-title"><CardArt art={card.art} size={48} /><span>{pick(card.name)}</span></span>} onClose={() => { if (purchasing === null) openConfirm(null); }} footer={
    <div className="purchase-actions">
      <span className="mono dim">{t("store.balance", { n: credits })}</span>
      <Button variant="ghost" disabled={purchasing !== null} onClick={() => openConfirm(null)}>{t("common.cancel")}</Button>
      <Button variant="gold" onClick={() => void buy(card.key)} data-testid="confirm-buy" disabled={purchasing !== null || card.locked !== null || (card.limit !== null && card.limit.used >= card.limit.max) || (card.expiresAt !== null && card.expiresAt <= now) || (card.currency === "CREDITS" && credits < card.price)} aria-busy={purchasing !== null}><Price price={card.price} currency={card.currency} /></Button>
    </div>
  }>
    <p className="hint">{card.double ? t("store.doubleHint") : t("store.confirmHint")}</p>
    <BundleList bundle={card.contents} />
  </Dialog>;
}

/** The purchase celebration: crate slam, item fly-in, confetti; clears itself. */
export function PurchaseFx() {
  const t = useT();
  const fx = useStore((s) => s.fx);
  const clear = useStore((s) => s.clearFx);
  useEffect(() => {
    if (!fx) return;
    audio.play("purchase");
    const id = window.setTimeout(clear, 2600);
    return () => window.clearTimeout(id);
  }, [fx, clear]);
  const pieces = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        i,
        x: (i * 37) % 100,
        d: (i * 13) % 7,
        c: ["var(--gold)", "var(--accent)", "var(--accent-2)", "#fff"][i % 4]!,
      })),
    [],
  );
  if (!fx) return null;
  const flightResource = Object.entries(fx.granted.resources ?? {}).find(([, amount]) => amount > 0)?.[0] as Resource | undefined;
  return (
    <div className="purchase-fx" onClick={clear} data-testid="purchase-fx">
      {flightResource ? <RewardFlight origin="[data-testid=purchase-crate]" target={`[data-testid=res-${flightResource}]`}><ResourceIcon resource={flightResource} size={28} /></RewardFlight> : fx.granted.credits ? <RewardFlight origin="[data-testid=purchase-crate]" target="[data-testid=credit-counter]"><ResourceIcon resource="CREDITS" size={28} /></RewardFlight> : null}
      {pieces.map((p) => (
        <span
          key={p.i}
          className="confetti"
          style={{ left: `${p.x}%`, animationDelay: `${p.d * 40}ms`, background: p.c }}
        />
      ))}
      <div className="crate-slam">
        <div className="crate" data-testid="purchase-crate">
          <AssetIcon id="itm.chest_gold" size={132} />
        </div>
        <div className="fx-title">{fx.doubled ? t("store.fx.double") : t("store.fx.title")}</div>
        <div className="fx-items">
          <BundleList bundle={fx.granted} />
        </div>
        {fx.nextStep ? (
          <div className="fx-next">
            {t("store.fx.next", { n: fx.nextStep.step ?? 2, price: fx.nextStep.price })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
